import moment from "moment";
import React, { FC, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useJwt } from "../../../../helpers/common";
import { toRelativeTime } from '../../../../utils/timeUtils';

// DataList Component
import {
  DataList,
  DataListColumn,
  SortConfig,
} from "../../../../components/common/DataList";

// Existing sub-components for cell rendering
import { Status } from "./sections";
import Progress from "./sections/Progress";
import Resources from "./sections/ProjectBoxComponent/Resources";
import WorkItemOptions from "./sections/WorkItemOptions";

// Utils
import {
  workUnitsTableHeaders
} from "../../../../constants/constants";
import {
  hasExpandableChildren
} from "../../../../utils/entityDisplayUtils";

// Icons (only additional ones not in entityTypeStyles)
import { FaGlasses } from "react-icons/fa";
import CustomTooltip from "../../../../components/common/CustomTooltip";
import { PriorityCell, StatusCell, TitleCell } from "../../../../shared/molecules/CadenceTable/CadenceTable";

interface ProjectListProps {
  data?: any[];
  handleProjectClick?: any;
  loading?: boolean;
  loadingMore?: boolean;
  onLoadMore?: () => void;
  hasMore?: boolean;
  visibleColumns?: any;
  setVisibleColumns?: (columns: Record<string, boolean>) => void;
  isLocalFilterApplied?: boolean;
  scrollRef?: React.RefObject<HTMLDivElement>;
  externalScroll?: boolean;
}

const ProjectList: FC<ProjectListProps> = ({
  data,
  handleProjectClick,
  visibleColumns,
  setVisibleColumns,
  loading = false,
  loadingMore = false,
  onLoadMore,
  hasMore = false,
  isLocalFilterApplied = false,
  scrollRef,
  externalScroll = false,
}) => {
  // Read localStorage ONCE on mount to prevent repeated reads and layout shifts
  const [cachedSavedColumns] = useState<Record<string, boolean> | null>(() => {
    if (typeof window === "undefined") return null;
    try {
      const saved = window.localStorage.getItem("projectListColumnVisibility");
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed && typeof parsed === "object") return parsed;
      }
    } catch (e) {
      console.warn("Failed to parse saved columns", e);
    }
    return null;
  });

  // Read saved column order from localStorage
  const [columnOrder, setColumnOrder] = useState<string[]>(() => {
    try {
      const saved = window.localStorage.getItem('projectListColumnOrder_v2');
      if (saved) {
        return JSON.parse(saved);
      }
    } catch (e) {
      console.error('Failed to parse column order', e);
    }
    // Default order from constants
    return workUnitsTableHeaders.map(h => h.value);
  });


  const [sortConfig, setSortConfig] = useState<SortConfig | null>(null);

  const jwtData: any = useJwt();
  const isAdmin =
    typeof (jwtData as any)?.org_role === "string" &&
    (jwtData as any).org_role?.toLowerCase() === "admin";
  const orgDetails = useSelector(
    (state: any) => state.organization.orgDetails || {},
  );
  const showCost = orgDetails.show_cost;
  const orgPriorities = orgDetails.priorities || [];

  // Effective visible columns (hide cost for non-admins)
  // Uses cached localStorage value during initial loading to prevent skeleton column jumps
  const effectiveVisibleColumns = useMemo(() => {
    // Start with either cached or current visible columns
    let base: Record<string, boolean>;
    if (loading && cachedSavedColumns) {
      base = { ...cachedSavedColumns };
    } else {
      base = { ...(visibleColumns || {}) };
    }

    // Always enforce admin/cost visibility rule to ensure consistent column count
    if (!(isAdmin && showCost)) {
      base.total_cost = false;
    }

    return base;
  }, [visibleColumns, isAdmin, showCost, loading, cachedSavedColumns]);

  // Column width calculation - based ONLY on effectiveVisibleColumns for consistency
  const columnWidths = useMemo(() => {
    const weights: { [key: string]: number } = {
      project_name: 28,
      status: 12,
      priority: 8,
      progress: 8,
      stage: 8,
      timeline: 12,
      modified_at: 10,
      integration_type: 10,
      integration_status: 10,
      members: 10,
      created_at: 10,
      created_by: 10,
      total_cost: 10,
      action: 8,
    };

    // Use current columnOrder for width calculation to ensure it matches visual order
    const orderedKeys = columnOrder.length > 0 ? columnOrder : workUnitsTableHeaders.map(h => h.value);

    const visibleColumnKeys = orderedKeys.filter(key => effectiveVisibleColumns[key]);

    if (visibleColumnKeys.length === 0) return {};

    const totalWeight = visibleColumnKeys.reduce(
      (sum, key) => sum + (weights[key] || 10),
      0,
    );
    const widths: { [key: string]: string } = {};
    visibleColumnKeys.forEach((key: any) => {
      const percentage = ((weights[key] || 10) / totalWeight) * 100;
      widths[key] = `${percentage}%`;
    });

    return widths;
  }, [effectiveVisibleColumns, isAdmin, columnOrder]);

  // Format currency helper
  const formatCurrency = (value: number) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(value);

  // Show loading when: loading prop is true
  const showLoading = loading;

  // Derive sorted data from props using useMemo (no duplicate state)
  const sortedProjects = useMemo(() => {
    if (!data || data.length === 0) return data || [];
    if (!sortConfig) return data;

    const { key, direction } = sortConfig;
    const lowerKey = key.toLowerCase();

    // Create array with indices for stable sort
    const indexedData = data.map((item, index) => ({ item, index }));

    return indexedData.sort((a, b) => {
      let valueA = a.item[key];
      let valueB = b.item[key];

      // Map to nested values for integration fields
      if (lowerKey === "integration_type" || lowerKey === "type") {
        valueA = a.item?.integration_data?.integration_item_type ?? "";
        valueB = b.item?.integration_data?.integration_item_type ?? "";
      } else if (lowerKey === "integration_status") {
        valueA = a.item?.integration_data?.integration_status ?? "";
        valueB = b.item?.integration_data?.integration_status ?? "";
      } else if (lowerKey === "total_cost") {
        valueA = a.item?.costs?.total_cost ?? 0;
        valueB = b.item?.costs?.total_cost ?? 0;
      } else if (lowerKey === "priority") {
        const p1 = orgPriorities.findIndex((p: any) => p.id === a.item?.priority);
        const p2 = orgPriorities.findIndex((p: any) => p.id === b.item?.priority);
        // If not found, place it at the end
        valueA = p1 !== -1 ? p1 : 999;
        valueB = p2 !== -1 ? p2 : 999;
      } else if (lowerKey === "project_name") {
        valueA = a.item?.name || a.item?.project_name || "";
        valueB = b.item?.name || b.item?.project_name || "";
      } else {
        if (valueA === undefined) {
          const matchingKey = Object.keys(a.item).find(
            (k) => k.toLowerCase() === lowerKey,
          );
          if (matchingKey) valueA = a.item[matchingKey];
        }
        if (valueB === undefined) {
          const matchingKey = Object.keys(b.item).find(
            (k) => k.toLowerCase() === lowerKey,
          );
          if (matchingKey) valueB = b.item[matchingKey];
        }
      }

      // Handle null/undefined values - push them to the end
      if (valueA == null && valueB == null) {
        // Both null - use original index for stability
        return a.index - b.index;
      }
      if (valueA == null) return 1; // A is null, push to end
      if (valueB == null) return -1; // B is null, push to end

      let comparison = 0;

      // String comparison
      if (typeof valueA === "string" && typeof valueB === "string") {
        comparison = valueA.toLowerCase().localeCompare(valueB.toLowerCase());
      }
      // Numeric comparison
      else {
        comparison = valueA > valueB ? 1 : valueA < valueB ? -1 : 0;
      }

      // If equal, use original index for stable sort
      if (comparison === 0) {
        return a.index - b.index;
      }

      // Apply direction
      return direction === "asc" ? comparison : -comparison;
    }).map(({ item }) => item); // Extract items from indexed wrapper
  }, [data, sortConfig]);

  // Handle sorting - just update sortConfig, sorting is derived via useMemo
  const handleSort = (key: string) => {
    const newDirection =
      sortConfig?.key === key && sortConfig?.direction === "asc"
        ? "desc"
        : "asc";
    setSortConfig({ key, direction: newDirection });
  };

  // Define columns with render functions - using original labels from workUnitsTableHeaders
  const columns: DataListColumn<any>[] = useMemo(() => {
    const cols: DataListColumn<any>[] = [];

    // Work Item (Project Name) column FIRST - with entity type icon and label
    cols.push({
      key: "project_name",
      label: "Work Item", // Changed from "Name" to "Work Item"
      skeletonShape: "workItem",
      render: (row) => {
        return (
          <TitleCell row={row}></TitleCell>
        );
      },
    });

    // Status column AFTER Work Item
    cols.push({
      key: "status",
      label: "Status",
      skeletonShape: "status",
      render: (row) => {
        return (
          <StatusCell row={row}></StatusCell>
        )
      },
    });
    // Status column AFTER Work Item
    cols.push({
      key: "priority",
      label: "Priority",
      skeletonShape: "badge",
      render: (row) => {
        return <PriorityCell priority={row.priority}></PriorityCell>;
      },
      sortable: true,
    });

    // Progress column
    cols.push({
      key: "progress",
      label: "Progress",
      skeletonShape: "progress",
      render: (row) => <Progress progress={row.progress} status={row.status} />,
    });

    // Total Cost column (admin only)
    cols.push({
      key: "total_cost",
      label: "Total Cost",
      skeletonShape: "badge",
      render: (row) => {
        if (!isAdmin || !showCost) return null; // Guard render just in case
        const totalCost = row.costs?.total_cost;
        if (totalCost !== undefined) {
          return (
            <span
              className={`inline-flex items-center px-2 py-1 rounded text-xs font-semibold ${
                totalCost >= 0
                  ? "text-green-700 dark:text-green-300 bg-green-50 dark:bg-green-900/20"
                  : "text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/20"
              }`}
            >
              {formatCurrency(totalCost)}
            </span>
          );
        }
        return (
          <span className="text-light-text-tertiary dark:text-gray-400">-</span>
        );
      },
    });

    // // Integration Type column - keep original label
    cols.push({
      key: "integration_type",
      label: "Integration Type",
      skeletonShape: "badge",
      render: (row) => {
        const type = row?.integration_data?.integration_item_type;
        if (type) {
          return (
            <span className="capitalize text-xs font-medium px-2.5 py-1 rounded bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-200">
              {type}
            </span>
          );
        }
        return (
          <span className="text-light-text-tertiary dark:text-gray-400">-</span>
        );
      },
    });

    // Integration Status column - keep original label
    cols.push({
      key: "integration_status",
      label: "Integration Status",
      skeletonShape: "text",
      skeletonWidth: "w-20",
      render: (row) => {
        const status = row?.integration_data?.integration_status;
        return (
          <span className="capitalize text-xs text-light-text-primary dark:text-white">
            {status || "-"}
          </span>
        );
      },
    });

    // Stage column
    cols.push({
      key: "stage",
      label: "Stage",
      skeletonShape: "badge",
      render: (row) => <Status stage={row.stage} />,
    });

    // Timeline column (start_date - end_date)
    cols.push({
      key: 'timeline',
      label: 'Timeline',
      skeletonShape: 'text',
      skeletonWidth: 'w-24',
      render: (row) => {
        const startFormatted = row.start_date ? moment(row.start_date).format('D MMM, YY') : null;
        const endFormatted = row.end_date ? moment(row.end_date).format('D MMM, YY') : null;
        
        let timeline = '—';
        if (startFormatted && endFormatted) {
          timeline = `${startFormatted} - ${endFormatted}`;
        } else if (startFormatted) {
          timeline = startFormatted;
        } else if (endFormatted) {
          timeline = endFormatted;
        }
        
        return (
          <span className="text-xs font-medium text-light-text-primary dark:text-white whitespace-nowrap">
            {timeline}
          </span>
        );
      },
    });

    // Last Modified column
    cols.push({
      key: 'modified_at',
      label: 'Last Modified',
      skeletonShape: 'text',
      skeletonWidth: 'w-20',
      render: (row) => (
        <span className="text-xs font-medium text-light-text-primary dark:text-white whitespace-nowrap">
          {row.modified_at ? toRelativeTime(row.modified_at) : '—'}
        </span>
      ),
    });

    // Members column - keep original label
    cols.push({
      key: "members",
      label: "Members",
      sortable: false,
      skeletonShape: "avatarGroup",
      render: (row) => (
        <Resources resources={row.members} projectId={row.project_id} />
      ),
    });

    // Created At column - keep original label
    cols.push({
      key: "created_at",
      label: "Created On",
      skeletonShape: "text",
      skeletonWidth: "w-20",
      render: (row) => (
        <span className="text-xs font-medium text-light-text-primary dark:text-white whitespace-nowrap">
          {row.created_at ? moment(row.created_at).format("D MMM, YY") : ""}
        </span>
      ),
    });

    // Created By column
    cols.push({
      key: "created_by",
      label: "Created By",
      sortable: false,
      skeletonShape: "avatarPadded",
      render: (row) => (
        <div className="">
          <Resources resources={[row.created_by]} projectId={row.project_id} />
        </div>
      ),
    });

    // Action column
    cols.push({
      key: "action",
      label: "",
      sortable: false,
      skeletonShape: "action",
      render: (row) => (
        <div className="flex items-center justify-end gap-0">
          {/* Sim Pending Flag - glasses icon matching card view */}
          {row.is_sim_pending && (
            <CustomTooltip title="Simulation present">
              <div className="-mr-3 w-6 h-8 bg-[#467966] rounded-bl-2xl rounded-br-2xl flex items-center justify-center pt-1 cursor-help">
                <FaGlasses className="text-white w-3 h-3" />
              </div>
            </CustomTooltip>
          )}
          <WorkItemOptions
            data={row}
            source="projects"
            onViewDetails={() => handleProjectClick?.(row)}
          />
        </div>
      ),
    });

    // Sort cols based on valid keys in columnOrder
    if (columnOrder && columnOrder.length > 0) {
      cols.sort((a, b) => {
        const idxA = columnOrder.indexOf(a.key);
        const idxB = columnOrder.indexOf(b.key);
        // If key found, use index. If not found (e.g. new column), put at end
        const valA = idxA !== -1 ? idxA : 999;
        const valB = idxB !== -1 ? idxB : 999;
        return valA - valB;
      });
    }

    return cols;
  }, [isAdmin, showCost, handleProjectClick, columnOrder]);

  // Track expanded rows for Advanced Epics
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  const handleToggleExpand = (rowId: string) => {
    setExpandedRows((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(rowId)) {
        newSet.delete(rowId);
      } else {
        newSet.add(rowId);
      }
      return newSet;
    });
  };

  // Check if row is expandable (Advanced Epic with child_entities)
  const isRowExpandable = (row: any) => hasExpandableChildren(row);

  // Get child rows (features) for an expandable row
  const getChildRows = (row: any) => row.child_entities || [];

  // --- Column Customization Handlers ---
  const handleColumnOrderChange = (newOrder: string[]) => {
    setColumnOrder(newOrder);
    localStorage.setItem('projectListColumnOrder_v2', JSON.stringify(newOrder));
  };

  const handleColumnVisibilityChange = (key: string) => {
    if (setVisibleColumns && visibleColumns) {
      const newVisible = {
        ...visibleColumns,
        [key]: !visibleColumns[key]
      };
      
      // Update local state (via parent prop)
      setVisibleColumns(newVisible);
      
      // Persist to localStorage
      localStorage.setItem('projectListColumnVisibility', JSON.stringify(newVisible));
    }
  };

  const handleResetColumns = () => {
    // Reset order
    const defaultOrder = workUnitsTableHeaders.map(h => h.value);
    setColumnOrder(defaultOrder);
    localStorage.removeItem('projectListColumnOrder_v2');

    // Reset visibility
    if (setVisibleColumns) {
      const defaultVisible: Record<string, boolean> = {};
      workUnitsTableHeaders.forEach(h => {
        defaultVisible[h.value] = true;
      });
      defaultVisible['action'] = true; // Ensure action is visible
      
      setVisibleColumns(defaultVisible);
      localStorage.setItem('projectListColumnVisibility', JSON.stringify(defaultVisible));
    }
  };

  // Columns to completely hide from the customizer (e.g. cost when not enabled)
  const hiddenColumnKeys = useMemo(() => {
    const keys: string[] = [];
    if (!(isAdmin && showCost)) {
      keys.push('total_cost');
    }
    return keys;
  }, [isAdmin, showCost]);

  return (
    <DataList
      data={sortedProjects}
      columns={columns}
      visibleColumns={effectiveVisibleColumns}
      columnWidths={columnWidths}
      loading={showLoading}
      loadingMore={loadingMore}
      hasMore={hasMore}
      onLoadMore={onLoadMore}
      onRowClick={handleProjectClick}
      onSort={handleSort}
      sortConfig={sortConfig}
      getRowKey={(row) => row.project_id || row.id}
      scrollRef={scrollRef}
      emptyMessage="No search results found."
      skeletonRows={12}
      // Expandable row props for Advanced Epics
      isRowExpandable={isRowExpandable}
      getChildRows={getChildRows}
      expandedRows={expandedRows}
      onToggleExpand={handleToggleExpand}
      // Column Customization
      onColumnOrderChange={handleColumnOrderChange}
      onColumnVisibilityChange={handleColumnVisibilityChange}
      onResetColumns={handleResetColumns}
      hiddenColumnKeys={hiddenColumnKeys}
      fixedColumnKeys={['project_name']}
      // @ts-ignore
      externalScroll={false}
    />
  );
};

export default ProjectList;
